import React from "react";
import { motion } from "framer-motion";
import { Clock4, PoundSterling } from "lucide-react";
import { CambridgeSummerConfig } from "../../../data/cambridgeSummerConfig";

interface IntroductionProps {
    config: CambridgeSummerConfig;
}

const Introduction: React.FC<IntroductionProps> = ({ config }) => {
    const { introduction } = config;

    return (
        <section
            className="py-20 md:py-28 bg-cover bg-center bg-no-repeat"
            style={{
                backgroundImage: "linear-gradient(135deg, #003C71 0%, #0066CC 50%, #003C71 100%)",
            }}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="text-3xl md:text-4xl lg:text-4xl font-black text-white mb-6 leading-tight max-w-lg">
                            {introduction.title}
                        </h2>

                        <div className="space-y-5">
                            {introduction.content.map((paragraph: string, index: number) => (
                                <motion.p
                                    key={index}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                    viewport={{ once: true }}
                                    className="text-lg text-[#FFFFFFCC] font-normal leading-relaxed"
                                >
                                    {paragraph}
                                </motion.p>
                            ))}
                        </div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.4 }}
                            viewport={{ once: true }}
                            className="mt-8 flex items-center gap-x-8 flex-wrap gap-y-4"
                        >
                            <div className="flex flex-col items-start gap-y-2">
                                <div className="flex items-center gap-x-2">
                                    <Clock4 className="w-6 h-6 text-[#9FCEF5]" />
                                    <p className="text-base font-normal text-[#D1D5DB]">Duration</p>
                                </div>
                                <p className="text-base font-bold text-white">2-4 weeks</p>
                            </div>
                            <div className="flex flex-col items-start gap-y-2">
                                <div className="flex items-center gap-x-2">
                                    <PoundSterling className="w-6 h-6 text-[#9FCEF5]" />
                                    <p className="text-base font-normal text-[#D1D5DB]">Registration</p>
                                </div>
                                <p className="text-base font-bold text-white">Applications Open</p>
                            </div>
                        </motion.div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, x: 50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                        className="relative hidden lg:block"
                    >
                        <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/10">
                            <img
                                src={introduction.sideImages}
                                alt="Cambridge University"
                                className="w-full h-80 object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#003C71]/60 to-transparent" />
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default Introduction;
